import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { PlanSection } from '@/components/PlanSection';
import { projectPlanData, systemRequirements, developmentSchedule, systemArchitecture, sampleTeams } from '@/data/projectPlan';
import { formatDate } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Users, Target, Layers, Database, Code, CheckCircle2, Clock, TrendingUp } from 'lucide-react';

const springPresets = {
  gentle: { type: 'spring' as const, stiffness: 300, damping: 35 },
  snappy: { type: 'spring' as const, stiffness: 400, damping: 30 },
};

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function Home() {
  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.CLASSROOM_1}
              alt="폴리텍 대학 스마트팩토리과"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <motion.div
            className="relative z-10 container mx-auto px-4 text-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <Badge className="mb-6 text-base px-6 py-2" variant="secondary">
              {projectPlanData.year}년 {projectPlanData.semester}학기
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              {projectPlanData.title}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              {projectPlanData.description}
            </p>
            <div className="flex flex-wrap gap-4 justify-center items-center text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(projectPlanData.startDate)} ~ {formatDate(projectPlanData.endDate)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>총 {projectPlanData.totalWeeks}주</span>
              </div>
            </div>
          </motion.div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2 variants={staggerItem} className="text-4xl font-bold mb-4">
                프로젝트 개요
              </motion.h2>
              <motion.p variants={staggerItem} className="text-xl text-muted-foreground max-w-2xl mx-auto">
                실무형 기술교육 중심의 스마트팩토리과 프로젝트 관리 시스템
              </motion.p>
            </motion.div>

            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <motion.div variants={staggerItem}>
                <Card className="h-full hover:shadow-lg transition-all duration-200">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>팀 구성</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      1학년 AI 프로젝트 4개조, 2학년 한이음드림업 3개조
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">1학년</span>
                        <Badge variant="secondary">4개조</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">2학년</span>
                        <Badge variant="secondary">3개조</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={staggerItem}>
                <Card className="h-full hover:shadow-lg transition-all duration-200">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Target className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>주요 기능</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                        <span className="text-sm">학생/팀 정보 관리</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                        <span className="text-sm">주차별 진도 체크</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                        <span className="text-sm">다양한 평가 항목 관리</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                        <span className="text-sm">실시간 대시보드</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={staggerItem}>
                <Card className="h-full hover:shadow-lg transition-all duration-200">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Layers className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>시스템 구조</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      {systemArchitecture.overview}
                    </p>
                    <div className="space-y-2">
                      {systemArchitecture.layers.map((layer, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium">{layer.name}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={staggerItem}>
                <Card className="h-full hover:shadow-lg transition-all duration-200">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <TrendingUp className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>평가 시스템</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      5가지 평가 항목으로 학생 성과 측정
                    </p>
                    <div className="space-y-2">
                      <Badge variant="outline" className="mr-2">출석</Badge>
                      <Badge variant="outline" className="mr-2">과제</Badge>
                      <Badge variant="outline" className="mr-2">발표</Badge>
                      <Badge variant="outline" className="mr-2">참여도</Badge>
                      <Badge variant="outline">팀 기여도</Badge>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-4">기술 스택</h2>
              <p className="text-xl text-muted-foreground">
                최신 웹 기술을 활용한 안정적이고 확장 가능한 시스템
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={springPresets.gentle}
              >
                <Card>
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Code className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>프론트엔드</CardTitle>
                    <CardDescription>사용자 인터페이스 기술</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {developmentSchedule.techStack.frontend.map((tech, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="font-medium">{tech.name}</span>
                          <span className="text-sm text-muted-foreground">{tech.description}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springPresets.gentle, delay: 0.1 }}
              >
                <Card>
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Database className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>백엔드</CardTitle>
                    <CardDescription>서버 및 데이터베이스</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {developmentSchedule.techStack.backend.map((tech, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="font-medium">{tech.name}</span>
                          <span className="text-sm text-muted-foreground">{tech.description}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ ...springPresets.gentle, delay: 0.2 }}
              >
                <Card>
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Layers className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle>개발 도구</CardTitle>
                    <CardDescription>빌드 및 배포 도구</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {developmentSchedule.techStack.tools.map((tech, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="font-medium">{tech.name}</span>
                          <span className="text-sm text-muted-foreground">{tech.description}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-4">개발 일정</h2>
              <p className="text-xl text-muted-foreground">
                체계적인 단계별 개발 프로세스
              </p>
            </motion.div>

            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {developmentSchedule.phases.map((phase, index) => (
                <motion.div key={phase.id} variants={staggerItem}>
                  <Card className="h-full hover:shadow-lg transition-all duration-200">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary">
                          {phase.startWeek}주차 ~ {phase.endWeek}주차
                        </Badge>
                        <span className="text-sm text-muted-foreground">{phase.duration}</span>
                      </div>
                      <CardTitle className="text-xl">{phase.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {phase.tasks.map((task, taskIndex) => (
                          <li key={taskIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                            <span>{task}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium mb-2">산출물:</p>
                        <div className="flex flex-wrap gap-2">
                          {phase.deliverables.map((deliverable, delIndex) => (
                            <Badge key={delIndex} variant="outline" className="text-xs">
                              {deliverable}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-4">주요 마일스톤</h2>
              <p className="text-xl text-muted-foreground">
                프로젝트 진행 단계별 핵심 목표
              </p>
            </motion.div>

            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="max-w-4xl mx-auto space-y-4"
            >
              {developmentSchedule.milestones.map((milestone, index) => (
                <motion.div key={milestone.id} variants={staggerItem}>
                  <Card className="hover:shadow-lg transition-all duration-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-primary">{milestone.week}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xl font-semibold">{milestone.name}</h3>
                            <Badge variant="secondary">{formatDate(milestone.date)}</Badge>
                          </div>
                          <p className="text-muted-foreground">{milestone.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={springPresets.gentle}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-4xl font-bold mb-6">프로젝트 목표</h2>
              <p className="text-xl text-muted-foreground mb-8">
                폴리텍 대학 스마트팩토리과의 프로젝트 관리를 효율적으로 지원하고,
                학생들의 학습 성과를 체계적으로 관리하는 통합 시스템 구축
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <Card>
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">효율적 관리</h3>
                    <p className="text-sm text-muted-foreground">
                      교수와 학생 모두 편리하게 사용할 수 있는 직관적인 인터페이스
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">실시간 모니터링</h3>
                    <p className="text-sm text-muted-foreground">
                      팀별 진도와 학생별 성과를 실시간으로 확인하고 관리
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Target className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">체계적 평가</h3>
                    <p className="text-sm text-muted-foreground">
                      다양한 평가 항목을 통한 공정하고 투명한 성과 측정
                    </p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
